import { clsx } from "clsx";
import { clamp as _clamp } from "ramda";
import {
  Children,
  type CSSProperties,
  forwardRef,
  type PropsWithChildren,
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { FocusRing, useMove } from "react-aria";

import { useDOMRef, useResizeObserver } from "../../hooks.ts";
import { useDataAttributes } from "../../hooks/useDataAttributes.ts";
import style from "./PanelGroup.module.css";
import { PanelOrientation, PanelPosition } from "./settings.ts";

export interface PanelGroupProps {
  /** Orientation of the children, not the grid. Inverted orientation. */
  orientation: PanelOrientation;
  fixedPanel: PanelPosition;
  /** If `undefined` it's 50%. With secondaryPanelHidden => true, it's 100% */
  defaultSize?: number | null;
  maxSize?: number;
  /** @default 100 */
  minSize?: number; // TODO We should have 2 child components: FixedPanel and FlexiblePanel, both with minSize
  /** Resize continuously. Use it only when rendering of the children is fast enough */
  isContinuous?: boolean;
  /** Happens when you released the mouse */
  onMoveEnd?: (size: number, max: number | undefined, isClosed: boolean) => void;
  /** Expose some function to open or hide the panels */
  apiRef?: Ref<PanelApi>;
  className?: string;
  style?: CSSProperties;
  defaultOpen?: boolean;
  /** @default 0 */
  closedSize?: number;
  // Prevent the user to resize the panels
  noResize?: boolean;
}

type MoveCb = (size: number, isClosed: boolean) => void;

export interface PanelApi {
  open: () => void;
  close: () => void;
  toggle: (cb?: (isOpen: boolean) => void) => void;
  onMove: (cb: MoveCb) => void;
  onMoveEnd: (cb: MoveCb) => void;
}

const IN_BETWEEN_SPACE = 1;
const DEFAULT_MIN_SIZE = 100;

/**
 * PanelGroup reacts to the change of Orientation so we keep both setup to not annoy people to resize all the time
 * [Horizontal, Vertical]
 * Will depends also on props.fixedPanel
 */
type Size = [number | undefined | null, number | undefined | null];

export const PanelGroup = forwardRef<HTMLDivElement, PropsWithChildren<PanelGroupProps>>(
  function PanelGroup(props, ref) {
    const domRef = useDOMRef(ref);
    const rect = useResizeObserver(domRef);
    const moveCbRef = useRef<MoveCb>(undefined);
    const moveEndCbRef = useRef<MoveCb>(undefined);

    const dataAttributes = useDataAttributes(props);

    const defaultSize = useMemo(() => {
      if (props.defaultSize != null) {
        if (props.closedSize != null) {
          return Math.max(props.defaultSize, props.closedSize);
        }

        return props.defaultSize;
      }

      return null;
    }, [props.defaultSize, props.closedSize]);

    /**
     * Sets Horizontal and Vertical position. Kept in memory if you switch orientation.
     * Don't use setHVSize directly, except to force values. Use setOrientationSize instead.
     */
    const [HVSize, setHVSize] = useState<Size>([defaultSize, defaultSize]);
    // For drag handle (useMove)
    const start = useRef<number | undefined | null>(undefined);
    const delta = useRef(0);
    /** Show the in-between bar */
    const [isResizing, setResizing] = useState(false);
    const hasBeenManuallyResized = useRef(false);
    const willClose = useRef(false);
    const [isOpen, setIsOpen] = useState(props.defaultOpen ?? true);

    /** Max width or height depending on the orientation */
    const max = useMemo(() => {
      const HVMax = rect ? [rect.width, rect.height] : undefined;
      const max = HVMax?.[props.orientation === PanelOrientation.Horizontal ? 0 : 1];

      return max != null ? max - IN_BETWEEN_SPACE : 0;
    }, [rect, props.orientation]);

    const maxSize = useMemo(() => {
      if (props.maxSize != null) {
        if (props.closedSize != null) {
          return Math.max(props.closedSize, props.maxSize);
        }

        return props.maxSize;
      }

      return max;
    }, [props.maxSize, props.closedSize, max]);

    const minSize = useMemo(
      () => Math.max(props.minSize ?? DEFAULT_MIN_SIZE, props.closedSize ?? DEFAULT_MIN_SIZE),
      [props.minSize, props.closedSize],
    );

    /** The 1 or 2 children. If no 2nd children, there is no handle */
    // FIXME Legacy. We should have 2 child components: FixedPanel and FlexiblePanel, both with minSize
    const [firstChild, secondChild]: [ReactNode, ReactNode] = useMemo(() => {
      const children = Children.toArray(props.children);
      return [children?.[0], children?.[1]];
    }, [props.children]);

    /** Clamp a value between minSize and maxSize (or 0 / 100% if not set) */
    const clamp = useCallback(
      (pos?: number | null) => {
        if (pos == null) return undefined;

        if (minSize > maxSize) {
          return maxSize;
        }

        return _clamp(minSize, maxSize, pos);
      },
      [minSize, maxSize],
    );

    const setOrientationSize = (size: number) => {
      setHVSize(([h, v]) => {
        const clampedSize = clamp(size)!;
        return props.orientation === PanelOrientation.Horizontal ? [clampedSize, v] : [h, clampedSize];
      });
    };

    const getOrientationSize = (): number | undefined | null => {
      if (!rect) return undefined;

      // We need to clamp here because when you resize the grid and hide the second child,
      // you may want to make it visible again. But the save size is too big the new rect size.
      return clamp(HVSize[props.orientation === PanelOrientation.Horizontal ? 0 : 1]);
    };

    /** --- Move Handler --- **/
    const { moveProps } = useMove({
      onMoveStart: () => {
        setResizing(true);
        if (isOpen) {
          start.current = getOrientationSize();
        } else {
          // `getOrientationSize` returns a clamped value and is never zero
          start.current = props.closedSize ?? 0;
          // Set it open only if continuous. If not it will push the slider by minSize.
          setIsOpen(props.isContinuous ?? false);
        }

        hasBeenManuallyResized.current = true;
      },
      onMove: (e) => {
        const move = props.orientation === PanelOrientation.Horizontal ? e.deltaX : e.deltaY;
        delta.current += move;

        if (start.current != undefined) {
          if (props.isContinuous) {
            // Move the Panels directly
            const move =
              props.fixedPanel === PanelPosition.Leading
                ? start.current + delta.current
                : start.current - delta.current;

            willClose.current = move * 2 - (props.closedSize ?? 0) < minSize;
            setIsOpen(!willClose.current);

            setOrientationSize(clamp(move)!);
            moveCbRef.current?.(clamp(move)!, willClose.current);
          } else {
            // Set a CSS variable to move the in-between bar without resizing the panels
            let move = delta.current;
            let _current = start.current;
            let _move = delta.current;
            if (props.fixedPanel === PanelPosition.Leading) {
              // const _current = start.current;
              const _max = maxSize;
              const _min = minSize;
              move = _clamp(_min, _max, _current + delta.current) - _current;
              _move = delta.current + _current;
            } else {
              // start.current is the width of the trailing panel, so max - panel width
              _current = max - start.current;
              const _max = max - minSize;
              const _min = max - maxSize;
              move = _clamp(_min, _max, _current + delta.current) - _current;
              _move = max - _current - delta.current;
            }

            willClose.current = _move * 2 < minSize;

            const closePosition = props.fixedPanel === PanelPosition.Leading ? -1 * _current : max - _current;

            domRef.current?.style.setProperty("--handler-move", `${willClose.current ? closePosition : move}px`); //delta.current
          }
        }
      },
      onMoveEnd: () => {
        // Because of parallel usage of ref and state, delta.current is already zero when setSize is executed.
        if (start.current != null) {
          const move =
            props.fixedPanel === PanelPosition.Leading
              ? clamp(start.current + delta.current)
              : clamp(start.current - delta.current); // Trailing => backward
          props.onMoveEnd?.(move!, max, willClose.current);
          moveCbRef.current?.(move!, willClose.current);
          moveEndCbRef.current?.(move!, willClose.current);
          if (!props.isContinuous) {
            // already set in onMove if continue
            setOrientationSize(move!);
          }
          delta.current = 0;
          domRef.current?.style.setProperty("--handler-move", "0px");
        }

        if (willClose.current) {
          if (start.current) setOrientationSize(start.current);
          setIsOpen(false);
        } else {
          setIsOpen(true);
        }

        setResizing(false);
      },
    });

    /** --- Exposed API --- **/
    useImperativeHandle(
      props.apiRef,
      () => ({
        open() {
          setIsOpen(true);
        },
        close() {
          setIsOpen(false);
        },
        toggle(cb) {
          setIsOpen((isOpen) => {
            const willOpen = !isOpen;
            cb?.(willOpen);

            return willOpen;
          });
        },
        onMove(cb) {
          moveCbRef.current = cb;
        },
        onMoveEnd(cb) {
          moveEndCbRef.current = cb;
        },
      }),
      [],
    );

    /** --- Effects --- **/
    useEffect(() => {
      // Set the Panels in the middles if no default size is provided
      if (defaultSize == null && !hasBeenManuallyResized.current) {
        /** Mid size in case there is not default size sets */
        const HVMid: Size = rect ? [Math.floor(rect.width / 2), Math.floor(rect.height / 2)] : [undefined, undefined];
        setHVSize(HVMid);
      }
    }, [defaultSize, rect]);

    return (
      <div
        className={clsx(style.grid, { [style.dragging]: isResizing }, props.className)}
        style={{ visibility: rect ? "visible" : "hidden", ...props.style }}
        ref={domRef}
        data-testid={dataAttributes["data-testid"] ? `${dataAttributes["data-testid"]}__panel-group` : "panel-group"}
        data-orientation={props.orientation}
      >
        <div
          className={style.pane}
          style={
            props.fixedPanel === PanelPosition.Leading && secondChild
              ? {
                  flexGrow: 0,
                  flexShrink: 0,
                  flexBasis: isOpen ? (getOrientationSize() ?? "50%") : (props.closedSize ?? 0),
                }
              : { flex: "1 1 auto" }
          }
          data-testid={
            dataAttributes["data-testid"]
              ? `${dataAttributes["data-testid"]}${props.fixedPanel === PanelPosition.Leading ? "__fixed_pane" : "__content_pane"}`
              : props.fixedPanel === PanelPosition.Leading
                ? "fixed_pane"
                : "content_page"
          }
        >
          {firstChild}
        </div>
        {!!secondChild && (
          <>
            {!props.noResize && (
              <FocusRing focusRingClass={style.focused}>
                <div className={style.grip} {...moveProps} tabIndex={0} />
              </FocusRing>
            )}
            <div
              className={style.pane}
              style={
                props.fixedPanel === PanelPosition.Trailing
                  ? {
                      flexGrow: 0,
                      flexShrink: 0,
                      flexBasis: isOpen ? (getOrientationSize() ?? "50%") : (props.closedSize ?? 0),
                    }
                  : { flex: "1 1 auto" }
              }
              data-testid={
                dataAttributes["data-testid"]
                  ? `${dataAttributes["data-testid"]}${props.fixedPanel === PanelPosition.Trailing ? "__fixed_pane" : "__content_pane"}`
                  : props.fixedPanel === PanelPosition.Trailing
                    ? "fixed_pane"
                    : "content_page"
              }
            >
              {secondChild}
            </div>
          </>
        )}
      </div>
    );
  },
);
